/**
 * Le suivi de l'habitant — chantier 7, étape 4 (maquettes 18 et 14).
 *
 * UNE seule source pour la carte « Ce que je suis » de l'accueil ET la page « Tout mon
 * suivi » : elles disent donc toujours la même chose. Trois démarches, avec leurs
 * statuts RÉELS (vérifiés le 24/09) :
 *   · signalements  : terminé dès qu'il est résolu (`resoluAt`) ;
 *   · urbanisme     : terminé si accordée, refusée ou annulée ;
 *   · locations     : terminé si retournée, refusée ou annulée.
 * Mêmes routes qu'avant : rien de nouveau côté serveur ni côté droits.
 */
import { api } from '@/lib/api';

export type TypeSuivi = 'signalement' | 'urbanisme' | 'location';
export type Ton = 'info' | 'warn' | 'ok' | 'neutre';
export interface LigneSuivi { id: string; type: TypeSuivi; titre: string; statut: string; ton: Ton; enCours: boolean; date: string; lien: string }

export const LIB_TYPE: Record<TypeSuivi, string> = { signalement: 'Signalements', urbanisme: 'Urbanisme', location: 'Locations' };
const URBA: Record<string, [string, Ton]> = {
  deposee: ['Déposée', 'info'], complete: ['Dossier complet', 'info'], incomplete: ['Pièces à fournir', 'warn'],
  instruction: ['En instruction', 'warn'], accordee: ['Accordée', 'ok'], refusee: ['Refusée', 'neutre'], annulee: ['Annulée', 'neutre'],
};
const LOC: Record<string, [string, Ton]> = {
  demande: ['Demandée', 'info'], validee: ['Confirmée', 'ok'], remise: ['En cours', 'info'],
  retournee: ['Terminée', 'neutre'], refusee: ['Refusée', 'neutre'], annulee: ['Annulée', 'neutre'],
};
const fin = (s: string) => ['accordee', 'refusee', 'annulee', 'retournee'].includes(s);

export async function chargerSuivi(): Promise<LigneSuivi[]> {
  const [sig, urb, loc] = await Promise.all([
    api.me.signalements.list().then((r) => r.items.map((s): LigneSuivi => ({
      id: `s-${s.id}`, type: 'signalement', titre: s.sujet, statut: s.statutLabel, ton: s.resoluAt ? 'ok' : 'info',
      enCours: !s.resoluAt, date: s.createdAt, lien: '/mes-signalements' }))).catch(() => [] as LigneSuivi[]),
    api.mesDemandesUrbanisme.list().then((r) => r.demandes.map((d): LigneSuivi => ({
      id: `u-${d.id}`, type: 'urbanisme', titre: `Demande ${d.numero}${d.adresseTerrain ? ` · ${d.adresseTerrain}` : ''}`,
      statut: URBA[d.statut]?.[0] ?? d.statut, ton: URBA[d.statut]?.[1] ?? 'info', enCours: !fin(d.statut),
      date: (d as { createdAt?: string }).createdAt ?? '', lien: '/mes-demandes-urbanisme' }))).catch(() => [] as LigneSuivi[]),
    api.materiel.reservations.list().then((r) => r.map((x): LigneSuivi => ({
      id: `l-${x.id}`, type: 'location', titre: `${x.materielNom} · ${new Date(x.dateRetrait).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`,
      statut: LOC[x.statut]?.[0] ?? x.statut, ton: LOC[x.statut]?.[1] ?? 'info', enCours: !fin(x.statut),
      date: x.dateRetrait, lien: '/mes-locations' }))).catch(() => [] as LigneSuivi[]),
  ]);
  return [...sig, ...urb, ...loc].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export const TON_CLASSE: Record<Ton, string> = {
  info: 'bg-tech/15 text-tech-fg', warn: 'bg-warning/15 text-warning', ok: 'bg-success/15 text-success', neutre: 'bg-surface-hi text-ink-muted',
};
